/**
 * The next set as it would be logged right now — PURE, and shared by the log
 * view's "Registrar série" and the rest notification's "Concluir série"
 * (`D209`, B9), which logs from the shade without the screen: both must
 * record the same numbers for the same session.
 */
import {
  type SetControls,
  secondMeasureOf,
  setControls,
} from "@repo/client/session";
import type { LiveWorkoutView } from "../store";
import type { SetMeasures, WorkoutSession } from "../store/session-model";

type CurrentExercise = NonNullable<LiveWorkoutView["current"]>;

/**
 * The controls of the exercise actually done: a substitute's own `tracks`,
 * so a timed one swapped in is logged as a hold, else reps and weight when
 * the swap does not carry them. Only the NEXT set reads this — a set logged
 * before a mid-exercise swap keeps the measures it was recorded with.
 */
export function controlsOf(current: CurrentExercise | undefined): SetControls {
  return setControls(
    current?.swap === undefined
      ? current?.item.exercise.tracks
      : current.swap.tracks
  );
}

/**
 * The measures `logSet` records; `load` is never `0` (bodyweight is `null`).
 * `rpe` is the exercise's effort, and only the last set carries it — `null`
 * leaves it out, as an unanswered question must.
 */
export function measuresOf(
  session: Pick<WorkoutSession, "kg" | "reps">,
  controls: SetControls,
  rpe: number | null
): SetMeasures {
  const load = controls.load && session.kg !== 0 ? session.kg : null;
  const effort = rpe === null ? {} : { rpe };
  const second = secondMeasureOf(controls);
  if (second === "hold") {
    return { durationSeconds: session.reps, load, reps: null, ...effort };
  }
  if (second === "distance") {
    return { distanceMetres: session.reps, load, reps: null, ...effort };
  }
  return { load, ...effort };
}
